/* eslint-disable camelcase, no-nested-ternary */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import SubmitButton from '../../common/SubmitButton';
import { fetchPaygradeAsync } from '../../../actions/BookingRequest';
import { getTime } from '../../../api/DataHandlers';
import { getDetailedRole, isDatesRange, validateValue, validateValues } from '../../common/Utils';
import { TRootState } from '../../../reducers';
import { TPaygrade, TServicesStructure } from '../../../types';
import { usePrevious } from '../../../hooks';

type TSummaryBarProps = {
    mode: 'checkout' | 'postjob';
    onClickBooking: () => void;
}

export default function SummaryBar({
    mode,
    onClickBooking,
}: TSummaryBarProps) {
    const dispatch = useDispatch();

    const {
        bookingRequest,
        roleData,
        paygrade,
        minStart,
        loadingPaygrade,
        servicesStructure,
    } = useSelector((state: TRootState) => ({
        bookingRequest: state.requestData.bookingRequest,
        roleData: state.requestData.roleData,
        paygrade: state.requestData.paygrade as TPaygrade,
        minStart: state.requestData.minStart,
        loadingPaygrade: state.loading.paygrade,
        servicesStructure: state.servicesStructure as TServicesStructure,
    }));

    const {
        isMultiple,
        dates,
        rangeDatesSelected,
        isRemoving,
        number_of_freelancers,
        service,
        date,
        duration,
        start_time,
        address1,
        address2,
        city,
        postcode,
    } = bookingRequest;

    const { client_pay_per_hour } = paygrade;

    const prevBookingRequest = usePrevious(bookingRequest);
    const prevRoleData = usePrevious(roleData);
    const prevDates = usePrevious(dates);
    const prevLoadingPaygrade = usePrevious(loadingPaygrade);
    const prevIsRemoving = usePrevious(isRemoving);

    useEffect(() => {
        let valid;
        if (
            prevLoadingPaygrade === loadingPaygrade &&
            prevIsRemoving === isRemoving &&
            (
                prevBookingRequest?.date !== date ||
                prevBookingRequest?.service !== service ||
                prevBookingRequest?.start_time !== start_time ||
                !isEqual(prevRoleData, roleData) ||
                !isEqual(prevDates, dates)
            )
        ) {
            if (isMultiple) {
                valid = validateValues([dates, service, start_time]);
            } else {
                valid = validateValues([date, service, start_time]);
            }

            if (valid) {
                const roleExtras = service ? roleData[service] : null;
                const start = !start_time ? getTime(minStart) : start_time;
                const newDate = isMultiple ? dates[0] : date;
                dispatch(fetchPaygradeAsync(service, start, newDate, roleExtras));
            }
        }
    }, [date, dates, service, start_time, roleData]);

    const selectedDates = isRemoving
        ? dates.filter((d: string) => !rangeDatesSelected.includes(d))
        : [...new Set([...dates, ...rangeDatesSelected])];
    const numberOfDaysSelected = selectedDates.length;
    const sortedDates = selectedDates.sort();
    const isRange = isDatesRange(sortedDates);
    const formattedDateRange = isRange
        ? `${moment(sortedDates[0]).format(
            'D',
        )} \u2013 ${moment(sortedDates[sortedDates.length - 1]).format(
            'D MMM YYYY',
        )}`
        : null;
    const detailedRole = getDetailedRole(service, roleData, servicesStructure);
    const formattedDate = isMultiple
        ? moment(dates[0]).format('DD MMM YYYY')
        : moment(date, 'YYYY-MM-DD').format('DD MMM YYYY');
    const showRole = detailedRole.length && validateValue(number_of_freelancers);
    const showTime = validateValues([date, start_time, duration]);
    const showLocation = validateValues([address1, city, postcode]);
    const showPrice = validateValues([client_pay_per_hour, number_of_freelancers, duration]);
    const showPriceMultiple = validateValues(
        [client_pay_per_hour, number_of_freelancers, duration, dates.length]
    );
    const bookingAvailable =
        mode === 'postjob' &&
        showRole &&
        showTime &&
        showLocation && ((showPrice && !isMultiple) || (showPriceMultiple && isMultiple));

    let totalPriceInfo = '';
    let vatPriceInfo = '';
    if (showPrice && !isMultiple) {
        const vatPrice = client_pay_per_hour * duration * number_of_freelancers * 0.2;
        vatPriceInfo = `(inc. £${vatPrice.toFixed(2)} VAT)`;
        const totalPrice = (client_pay_per_hour * duration * number_of_freelancers) + vatPrice;
        totalPriceInfo = totalPrice.toFixed(2);
    } else if (showPriceMultiple) {
        const vatPrice = client_pay_per_hour * duration * number_of_freelancers * 0.2 * selectedDates.length;
        vatPriceInfo = `(inc. £${ vatPrice.toFixed(2) } VAT)`;
        const totalPrice = (client_pay_per_hour * duration * number_of_freelancers * selectedDates.length) + vatPrice;
        totalPriceInfo = totalPrice.toFixed(2);
    }

    const getRoleIcon = () => {
        if (!service) {
            return 'icon-help-circle';
        }
        // @ts-ignore
        const roleFieldName = servicesStructure[service]?.role_field_name;
        // @ts-ignore
        const roleFilterName = servicesStructure[service]?.role_filter_name;
        // @ts-ignore
        return servicesStructure[service][roleFilterName][roleData[service][roleFieldName]].icon_name;
    };

    return (
        <div>
            <div className="summarybar visible-xs-flex">
                <div className="summary-item summary-item--delivery">
                    <i className={`icon ${getRoleIcon()}`} />
                    {showRole ? (
                        <div>
                            {`${number_of_freelancers} ×`}
                            <br />
                            {detailedRole}
                            <br />
                            {loadingPaygrade ? (
                                <i className="icon icon-loading-1" />
                            ) : validateValue(client_pay_per_hour) ? (
                                <span>
                                    £{client_pay_per_hour.toFixed(2)} /h
                                </span>
                            ) : false}
                        </div>
                    ) : false}
                </div>
                <div className="summary-item summary-item--datetime">
                    <i className="icon icon-calendar-1" />
                    {showTime ? (
                        <div>
                            {isMultiple && numberOfDaysSelected !== 1
                                ? isRange
                                    ? formattedDateRange
                                    : `${numberOfDaysSelected} days selected`
                                : formattedDate}
                            <br />
                            at {start_time} for
                            <br />
                            {duration} hours
                        </div>
                    ) : false}
                </div>
                <div className="summary-item summary-item--address">
                    <i className="icon icon-location-pin-8" />
                    {showLocation ? (
                        <div>
                            <p>{address1}</p>
                            <p>{address2}</p>
                            <p>{`${city}, ${postcode}`}</p>
                        </div>
                    ) : false}
                </div>
                <div className="summary-item summary-item--price">
                    <i className="icon icon-pound-sign-circle" />
                    {showPrice || showPriceMultiple ? (
                        <div>
                            {loadingPaygrade ? (
                                <i className="icon icon-loading-1" />
                            ) : (
                                <span>
                                    £{totalPriceInfo}
                                    <br />
                                </span>
                            )}
                            <span>{vatPriceInfo}</span>
                        </div>
                    ) : false}
                </div>
                <SubmitButton
                    className={`complete-booking${bookingAvailable ? '' : ' disabled'}`}
                    onClick={onClickBooking}
                    propName="booking"
                >
                    CONTINUE
                </SubmitButton>
            </div>
            <div className="summarybar hidden-xs">
                {showRole ? (
                    <p className="summary-item summary-item--delivery">
                        {`${number_of_freelancers} ×`}
                        <br />
                        {detailedRole}
                        <br />
                        {loadingPaygrade ? (
                            <i className="icon icon-loading-1" />
                        ) : validateValue(client_pay_per_hour) ? (
                            <span>
                                £{client_pay_per_hour.toFixed(2)} /h
                            </span>
                        ) : false}
                    </p>
                ) : (
                    <p className="summary-item summary-item--delivery">
                        No Role Specified
                    </p>
                )}
                <span className="item-separator" />
                {showTime ? (
                    <p className="summary-item summary-item--datetime">
                        {isMultiple && numberOfDaysSelected !== 1
                            ? isRange
                                ? formattedDateRange
                                : `${numberOfDaysSelected} days selected`
                            : formattedDate}
                        <br />
                        at {start_time} for
                        <br />
                        <span>{duration} hours</span>
                    </p>
                ) : (
                    <p className="summary-item summary-item--datetime">
                        No Time Specified
                    </p>
                )}
                <span className="item-separator" />
                {showLocation ? (
                    <div className="summary-item summary-item--address">
                        <p>{address1}</p>
                        <p>{address2}</p>
                        <p>{`${city}, ${postcode}`}</p>
                    </div>
                ) : (
                    <div className="summary-item summary-item--address">
                        No Location Specified
                    </div>
                )}
                <span className="item-separator" />
                {showPrice || showPriceMultiple ? (
                    <p className="summary-item summary-item--price">
                        Total:&nbsp;
                        {loadingPaygrade ? (
                            <i className="icon icon-loading-1" />
                        ) : (
                            <span>£{totalPriceInfo}</span>
                        )}
                        <br />
                        <span>{vatPriceInfo}</span>
                    </p>
                ) : false}
                <SubmitButton
                    className={`complete-booking${bookingAvailable ? '' : ' disabled'}`}
                    onClick={onClickBooking}
                    propName="booking"
                >
                    CONTINUE
                </SubmitButton>
            </div>
        </div>
    );
}
